import type { FundOwnershipDetailModel } from 'api';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { waterfallFundSummaryRowConfigProps } from 'pages/Waterfall/SpreadsheetConfig/FundSummarySheetConfig/FundSummaryRowConfig/waterfallFundSummaryRowConfig.props';
import {
  AggregatedFundOwnershipDetail
} from 'pages/Waterfall/SpreadsheetConfig/FundSummarySheetConfig/FundSummaryRowConfig/WaterfallFundSummaryRowConfig.types';
import {
  WaterfallFundSummaryColumn, WaterfallFundSummaryTableData
} from 'pages/Waterfall/SpreadsheetConfig/FundSummarySheetConfig/useFundSummarySheetConfig/useWaterfallFundSummarySheetConfig.types';
import { toNumberOrZero } from 'utilities/toNumberOrZero/toNumberOrZero';


// Builds row config with a generic title row, aggregated data rows, and a totals row
export const WaterfallFundSummaryRowConfig = (props: waterfallFundSummaryRowConfigProps): ICell<AggregatedFundOwnershipDetail, object, WaterfallFundSummaryColumn, WaterfallFundSummaryTableData>[] => {
  const details = props.captable.fund_ownership?.fund_ownership_detail;
  const {fundIrrMap} = props
  if (!details) {
    return [];
  }
  const related_entity_fod_map = details?.filter(fod => fod.related_entity)?.reduce((acc, fod) => {
    if (acc[fod.related_entity as number]) {
      acc[fod.related_entity as number].push(fod);
    } else {
      acc[fod.related_entity as number] = [fod];
    }
    return acc;
  }, {} as {[key: number]: FundOwnershipDetailModel[]}) || {};

  const map = new Map<string, AggregatedFundOwnershipDetail>();

  details.forEach(detail => {
    const isFund = detail.fund != null && detail.fund !== undefined;
    const idNum = detail.fund
    if (idNum == null) {
      return;
    }
    const key = `${isFund ? 'fund' : 'rel'}_${idNum}`;

    const invested = toNumberOrZero(detail.invested_capital);
    const cash = toNumberOrZero(detail.cash_distribution);
    const shares = toNumberOrZero(detail.shares_distribution);
    const realized = cash + shares;

    const existing = map.get(key);
    if (!existing) {
      map.set(key, {
        id: idNum,
        fund: isFund ? idNum : null,
        related_entity: !isFund ? idNum : null,
        invested_capital: invested.toString(),
        cash_distribution: cash.toString(),
        shares_distribution: shares.toString(),
        realized_value: realized.toString(),
        related_reference_set: (detail.entity_reference != null && related_entity_fod_map[detail.entity_reference as unknown as number]) ? [detail.entity_reference as unknown as number] : [],
        irr: isFund ? fundIrrMap?.[detail.fund as number]: 0,
      } as AggregatedFundOwnershipDetail);
    } else {
      const nextInvested = toNumberOrZero(existing.invested_capital) + invested;
      const nextCash = toNumberOrZero(existing.cash_distribution) + cash;
      const nextShares = toNumberOrZero(existing.shares_distribution) + shares;
      existing.invested_capital = nextInvested.toString();
      existing.cash_distribution = nextCash.toString();
      existing.shares_distribution = nextShares.toString();
      existing.realized_value = (nextCash + nextShares).toString();
      // merge entity_reference into related_reference_set, ensuring uniqueness and presence in related_entity_fod_map
      const currentSet = existing.related_reference_set ?? [];
      const ref = detail.entity_reference as unknown as number | null;
      const incoming = (ref != null && related_entity_fod_map[ref]) ? [ref] : [];
      if (incoming.length) {
        const merged = new Set<number>([...currentSet, ...incoming]);
        existing.related_reference_set = Array.from(merged);
      }
    }
  });

  const aggregated: AggregatedFundOwnershipDetail[] = Array.from(map.values());

  // Build rows: header placeholder + data + totals placeholder
  const rows: ICell<AggregatedFundOwnershipDetail, object, WaterfallFundSummaryColumn, WaterfallFundSummaryTableData>[] = [];

  // Title/header placeholder row (parser will render column headers)
  rows.push({} as ICell<AggregatedFundOwnershipDetail, object, WaterfallFundSummaryColumn, WaterfallFundSummaryTableData>);

  // Data rows
  rows.push(
    ...aggregated.map(data => ({ data } as ICell<AggregatedFundOwnershipDetail, object, WaterfallFundSummaryColumn, WaterfallFundSummaryTableData>))
  );

  // Totals placeholder row (parser will compute totals)
  rows.push({} as ICell<AggregatedFundOwnershipDetail, object, WaterfallFundSummaryColumn, WaterfallFundSummaryTableData>);

  return rows;
}